import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Spin, message } from 'antd';
import { PaymentAPI } from '../../shared/api/conversationService';
import './PaymentResult.css';

/**
 * 支付结果页面组件
 */
const PaymentResult: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [status, setStatus] = useState<'success' | 'failure' | 'cancel' | 'loading'>('loading');
  const [paymentInfo, setPaymentInfo] = useState<any>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);

  useEffect(() => {
    checkPaymentStatus();
  }, [location]);

  const checkPaymentStatus = async () => {
    try {
      // 从URL参数获取支付信息
      const stripeSessionId = searchParams.get('session_id');
      const paymentId = searchParams.get('payment_id');
      
      // 从URL路径判断初始状态
      let initialStatus: 'success' | 'failure' | 'cancel' = 'success';
      if (location.pathname.includes('cancel')) {
        initialStatus = 'cancel';
      } else if (location.pathname.includes('failure')) {
        initialStatus = 'failure';
      }

      // 如果有支付ID或会话ID，查询具体的支付状态
      if (paymentId || stripeSessionId) {
        try {
          const queryId = paymentId || stripeSessionId;
          if (!queryId) return;
          
          const paymentStatus = await PaymentAPI.getPaymentStatus(queryId);
          
          if (paymentStatus) {
            setPaymentInfo(paymentStatus);
            setSessionId(paymentStatus.session_id);
            
            // 根据实际支付状态设置页面状态
            switch (paymentStatus.status) {
              case 'succeeded':
              case 'completed':
                setStatus('success');
                break;
              case 'failed':
                setStatus('failure');
                break;
              case 'cancelled':
              case 'canceled':
                setStatus('cancel');
                break;
              default:
                // 如果状态还是pending，根据URL路径判断
                setStatus(initialStatus);
            }
          } else {
            // 如果查询不到支付信息，根据URL路径判断
            setStatus(initialStatus);
          }
        } catch (error) {
          console.error('查询支付状态失败:', error);
          // 查询失败时根据URL路径判断
          setStatus(initialStatus);
        }
      } else {
        // 没有支付ID时，根据URL路径判断
        setStatus(initialStatus);
      }
    } catch (error) {
      console.error('处理支付结果失败:', error);
      setStatus('failure');
    }
  };

  // 返回首页
  const handleBackToHome = () => {
    navigate('/');
  };

  // 返回支付页面
  const handleRetryPayment = () => {
    if (sessionId) {
      navigate('/payment', { state: { sessionId } });
    } else {
      navigate('/payment');
    }
  };

  // 继续会话
  const handleContinueChat = () => {
    if (sessionId) {
      navigate(`/chat?session_id=${sessionId}`);
    } else {
      navigate('/chat');
    }
  };

  if (status === 'loading') {
    return (
      <div className="payment-result-container">
        <div className="payment-result-card">
          <Spin size="large" />
          <p>{t('payment.result.checking')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-result-container">
      <div className="payment-result-card">
        {status === 'success' && (
          <>
            <div className="result-icon success">
              <i className="fas fa-check-circle"></i>
            </div>
            <h2>{t('payment.success.title')}</h2>
            <p>{t('payment.success.message')}</p>
            {paymentInfo && (
              <div className="payment-details">
                <p><strong>{t('payment.details.amount')}:</strong> ¥{paymentInfo.amount_jpy?.toLocaleString()}</p>
                <p><strong>{t('payment.details.method')}:</strong> {t(`payment.method.${paymentInfo.payment_method}`)}</p>
                {paymentInfo.paid_at && (
                  <p><strong>{t('payment.details.paidAt')}:</strong> {new Date(paymentInfo.paid_at).toLocaleString()}</p>
                )}
              </div>
            )}
            <div className="button-group">
              {sessionId && (
                <button className="primary-button" onClick={handleContinueChat}>
                  {t('payment.success.continueChat')}
                </button>
              )}
              <button className="secondary-button" onClick={handleBackToHome}>
                {t('payment.success.backToHome')}
              </button>
            </div>
          </>
        )}

        {status === 'cancel' && (
          <>
            <div className="result-icon cancel">
              <i className="fas fa-times-circle"></i>
            </div>
            <h2>{t('payment.cancel.title')}</h2>
            <p>{t('payment.cancel.message')}</p>
            {paymentInfo && (
              <div className="payment-details">
                <p><strong>{t('payment.details.amount')}:</strong> ¥{paymentInfo.amount_jpy?.toLocaleString()}</p>
                <p><strong>{t('payment.details.method')}:</strong> {t(`payment.method.${paymentInfo.payment_method}`)}</p>
              </div>
            )}
            <div className="button-group">
              <button className="primary-button" onClick={handleRetryPayment}>
                {t('payment.cancel.retry')}
              </button>
              <button className="secondary-button" onClick={handleBackToHome}>
                {t('payment.cancel.backToHome')}
              </button>
            </div>
          </>
        )}

        {status === 'failure' && (
          <>
            <div className="result-icon failure">
              <i className="fas fa-exclamation-circle"></i>
            </div>
            <h2>{t('payment.failure.title')}</h2>
            <p>{t('payment.failure.message')}</p>
            {paymentInfo?.failure_reason && (
              <div className="error-details">
                <p><strong>{t('payment.failure.reason')}:</strong> {paymentInfo.failure_reason}</p>
              </div>
            )}
            {paymentInfo && (
              <div className="payment-details">
                <p><strong>{t('payment.details.amount')}:</strong> ¥{paymentInfo.amount_jpy?.toLocaleString()}</p>
                <p><strong>{t('payment.details.method')}:</strong> {t(`payment.method.${paymentInfo.payment_method}`)}</p>
              </div>
            )}
            <div className="button-group">
              <button className="primary-button" onClick={handleRetryPayment}>
                {t('payment.failure.retry')}
              </button>
              <button className="secondary-button" onClick={handleBackToHome}>
                {t('payment.failure.backToHome')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentResult; 